import React, { useEffect, useState, useRef } from 'react'
import { Navigate, Outlet, useParams } from 'react-router-dom'
import Loading from '../components/Loading'
import NotFound from '../pages/NotFound'
import { useAuthStore } from '../stores/authStore'
import { isNativeShell, syncAuthTokens, isDigitalMode } from '@renderer/lib/runtime'
import { resolveDigitalProjectId } from '@renderer/lib/digitalProject'

interface User {
  _id: string
  email: string
  username: string
  sessionCredits: number
  isBahelaCollaborator?: boolean
}

// Cookie initialization is now handled in App.tsx bootstrap

// Cache user per projectId to avoid unnecessary re-fetches
const userCache = new Map<string, { user: User; timestamp: number }>()
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

// Track in-flight requests to prevent duplicates
const inFlightRequests = new Map<string, AbortController>()

// Retry configuration
const MAX_RETRIES = 3
const RETRY_DELAYS = [1000, 2000, 4000] // Exponential backoff: 1s, 2s, 4s

const ProtectedRoute: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const initializeCookie = useAuthStore((state) => state.initializeCookie)
  const fetchAbortControllerRef = useRef<AbortController | null>(null)



  // Attempt to refresh token when auth error occurs
  const attemptTokenRefresh = async (): Promise<boolean> => {
    try {
      console.log('🔄 [AUTH] Attempting token refresh...')
      await initializeCookie()
      // Small delay to ensure cookies are synced
      await new Promise((resolve) => setTimeout(resolve, 200))
      return true
    } catch (error) {
      console.warn('⚠️ [AUTH] Token refresh failed:', error)
      return false
    }
  }

  // Fetch user with retry logic and exponential backoff
  const fetchUserWithRetry = async (
    attempt: number = 0,
    abortController?: AbortController
  ): Promise<User | null> => {
    // Digital-mode bypass: ANY public project (resolved by slug or, for
    // superadmin QA, by raw ObjectId) skips the user fetch and uses a
    // project-token cookie instead — same mechanism as the hardcoded
    // `bypassIds` below, but broader trigger. See resolveDigitalProjectId.
    if (isDigitalMode() && projectId) {
      const resolvedId = await resolveDigitalProjectId(projectId, abortController?.signal)
      if (resolvedId) {
        try {
          const tokenResponse = await fetch(`${process.env.VITE_API_URL}/api/projects/token`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ projectId: resolvedId }),
            signal: abortController?.signal
          })
          if (!tokenResponse.ok) throw new Error('Failed to get project token')

          try {
            await syncAuthTokens()
          } catch (error) {
            console.warn('[AUTH] Failed to sync project token to main process:', error)
          }

          // Synthetic bypass user — matches the shape used by the legacy
          // `bypassIds` branch below. Downstream code (ProjectRoute, the
          // session store) only reads _id / email / username / sessionCredits.
          return {
            _id: '6790cf47674d6ab64bfefd3d',
            email: 'dreambooth.team@gmail.com',
            username: 'dreambooth.team',
            sessionCredits: 99999999
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') throw error
          console.error('[AUTH] Digital-mode public-project bypass failed:', error)
          // Don't fall through to operator auth in digital mode — there's
          // no operator account here. Return null and let the render path
          // resolve to NotFound via the digital-aware guard below.
          return null
        }
      }
      // No slug match / not public → no bypass, no user. Caller renders
      // NotFound (see digital-aware fallback at the bottom of this file).
      return null
    }

    const bypassIds = [
      '67e3f8d12bb1bd3dbad28011',
      '67e3f8e32bb1bd3dbad28012',
      '67e3f8f02bb1bd3dbad28013'
    ]

    if (bypassIds.includes(projectId || '')) {
      try {
        // Request project-auth token for public projects
        const tokenResponse = await fetch(`${process.env.VITE_API_URL}/api/projects/token`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ projectId }),
          signal: abortController?.signal
        })

        if (!tokenResponse.ok) {
          throw new Error('Failed to get project token')
        }

        try {
          await syncAuthTokens()
        } catch (error) {
          console.warn('[AUTH] Failed to sync project token to main process:', error)
        }

        // After getting the token, set the bypass user
        return {
          _id: '6790cf47674d6ab64bfefd3d',
          email: 'dreambooth.team@gmail.com',
          username: 'dreambooth.team',
          sessionCredits: 99999999
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          throw error // Don't retry aborted requests
        }
        console.error('Failed to get project token:', error)
        // Retry with backoff
        if (attempt < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]))
          return fetchUserWithRetry(attempt + 1, abortController)
        }
        return null
      }
    }

    try {
      const response = await fetch(`${process.env.VITE_API_URL}/api/users`, {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        signal: abortController?.signal
      })

      if (response.ok) {
        const data = await response.json()
        // Cache the user
        if (projectId) {
          userCache.set(projectId, { user: data, timestamp: Date.now() })
        }
        return data
      }

      // Handle different error statuses
      const isServerError = response.status >= 500
      const isAuthError = response.status === 401 || response.status === 403

      // For auth errors, try token refresh before giving up
      if (isAuthError && attempt === 0) {
        console.log('🔄 [AUTH] Auth error detected, attempting token refresh...')
        const refreshSuccess = await attemptTokenRefresh()
        if (refreshSuccess) {
          // Retry the request after refresh
          return fetchUserWithRetry(attempt + 1, abortController)
        }
      }

      // Offline-first fallback: reuse the cached operator ONLY when the server is
      // unreachable / erroring (5xx), NOT when it explicitly rejected the token.
      //
      // A 401/403 means the server WAS reached and the token is genuinely invalid
      // (cleared, expired, or device-mismatched). Falling back to the cached user
      // there is what let a booth keep running with a dead token — capturing photos
      // that can never upload while the UI still showed people as served. So on a
      // definitive auth rejection we do NOT reuse the cache: fall through to a
      // redirect to /login and force re-authentication before any session runs.
      // (True offline is a thrown fetch, handled in the catch below — not a 401.)
      if (isServerError) {
        const authStore = useAuthStore.getState()
        if (authStore.user && authStore.isAuthenticated) {
          console.log('📦 [AUTH] Server error (5xx), using cached user from auth store')
          console.log('✅ [AUTH] Cached user found:', {
            email: authStore.user.email,
            name: authStore.user.name
          })
          return {
            _id: 'offline-user',
            email: authStore.user.email,
            username: authStore.user.name || authStore.user.email,
            sessionCredits: 0
          }
        }
      }

      if (isAuthError) {
        console.warn(
          '🚫 [AUTH] Server rejected the token (401/403) — refusing to run the booth on an invalid token; redirecting to login'
        )
      }

      // Retry on server errors or if we haven't exhausted retries
      if (isServerError && attempt < MAX_RETRIES) {
        console.log(`🔄 [AUTH] Server error, retrying (attempt ${attempt + 1}/${MAX_RETRIES})...`)
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]))
        return fetchUserWithRetry(attempt + 1, abortController)
      }

      // Only return null if no cached user and all retries exhausted
      return null
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw error // Don't retry aborted requests
      }

      console.error('Failed to fetch user:', error)
      console.log('🔍 [AUTH] Fetch failed, checking for cached user...')

      // When network fails, check if we have a cached user in auth store
      if (isNativeShell()) {
        const authStore = useAuthStore.getState()
        if (authStore.user && authStore.isAuthenticated) {
          console.log('📦 [AUTH] Network unavailable, using cached user from auth store')
          console.log('✅ [AUTH] Cached user found:', {
            email: authStore.user.email,
            name: authStore.user.name
          })
          return {
            _id: 'offline-user',
            email: authStore.user.email,
            username: authStore.user.name || authStore.user.email,
            sessionCredits: 0
          }
        } else {
          console.warn('⚠️ [AUTH] No cached user found in auth store')
        }
      } else {
        console.log('❌ [AUTH] Not in Electron environment or no cached user available')
      }

      // Retry on network errors if we haven't exhausted retries
      if (attempt < MAX_RETRIES) {
        console.log(`🔄 [AUTH] Network error, retrying (attempt ${attempt + 1}/${MAX_RETRIES})...`)
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]))
        return fetchUserWithRetry(attempt + 1, abortController)
      }

      return null
    }
  }

  // Fetch user (cookie is already initialized in App.tsx bootstrap)
  useEffect(() => {

    // Check cache first
    if (projectId && userCache.has(projectId)) {
      const cached = userCache.get(projectId)!
      const age = Date.now() - cached.timestamp
      if (age < CACHE_TTL) {
        console.log('📦 [AUTH] Using cached user data')
        setUser(cached.user)
        setIsLoading(false)
        return
      } else {
        // Cache expired, remove it
        userCache.delete(projectId)
      }
    }

    // Cancel any in-flight request for this projectId
    const requestKey = projectId || 'default'
    if (inFlightRequests.has(requestKey)) {
      inFlightRequests.get(requestKey)?.abort()
      inFlightRequests.delete(requestKey)
    }

    // Create new abort controller for this request
    const abortController = new AbortController()
    inFlightRequests.set(requestKey, abortController)
    fetchAbortControllerRef.current = abortController

    const fetchUser = async (): Promise<void> => {
      try {
        const fetchedUser = await fetchUserWithRetry(0, abortController)
        if (abortController.signal.aborted) {
          return // Request was cancelled
        }
        setUser(fetchedUser)
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('🛑 [AUTH] Request was cancelled')
          return // Request was cancelled, don't update state
        }
        console.error('❌ [AUTH] Failed to fetch user after retries:', error)
        setUser(null)
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
        inFlightRequests.delete(requestKey)
      }
    }

    fetchUser()

    // Cleanup: abort request if component unmounts or dependencies change
    return (): void => {
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort()
      }
      inFlightRequests.delete(requestKey)
    }
  }, [projectId])

  if (isLoading) {
    return <Loading />
  }

  if (user) {
    return <Outlet context={{ user }} />
  }

  // Digital mode has no operator login flow — surface 404 instead of a
  // redirect to /login. Reaching this branch means either the slug
  // didn't resolve, or the project exists but isn't `isPublic`.
  if (isDigitalMode()) {
    return <NotFound />
  }

  return <Navigate to={`/login?callbackUrl=${window.location.pathname}`} />
}

export default ProtectedRoute
